import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useAiDictationStore from '../stores/aiDictationStore';
import StatusBar from '../components/StatusBar';
import NumberAdjuster from '../components/NumberAdjuster';

const AiDictationRandom = () => {
  const navigate = useNavigate();
  const { setWords, setSourceType } = useAiDictationStore();
  
  // 设置来源类型
  React.useEffect(() => {
    setSourceType('random');
  }, [setSourceType]);

  // 随机选择设置
  const [wordCount, setWordCount] = useState(7);
  const [difficulty, setDifficulty] = useState(3);
  
  // 模拟随机单词库
  const randomWordPool = [
    // 简单单词
    { id: 1, word: 'cat', difficulty: 1 },
    { id: 2, word: 'dog', difficulty: 1 },
    { id: 3, word: 'red', difficulty: 1 },
    { id: 4, word: 'blue', difficulty: 1 },
    { id: 5, word: 'big', difficulty: 1 },
    { id: 6, word: 'small', difficulty: 1 },
    
    // 中等难度单词
    { id: 7, word: 'apple', difficulty: 2 },
    { id: 8, word: 'banana', difficulty: 2 },
    { id: 9, word: 'orange', difficulty: 2 },
    { id: 10, word: 'teacher', difficulty: 2 },
    { id: 11, word: 'student', difficulty: 2 },
    { id: 12, word: 'school', difficulty: 2 },
    
    // 较难单词
    { id: 13, word: 'beautiful', difficulty: 3 },
    { id: 14, word: 'important', difficulty: 3 },
    { id: 15, word: 'different', difficulty: 3 },
    { id: 16, word: 'computer', difficulty: 3 },
    { id: 17, word: 'elephant', difficulty: 3 },
    { id: 18, word: 'mountain', difficulty: 3 },
    
    // 困难单词
    { id: 19, word: 'environment', difficulty: 4 },
    { id: 20, word: 'technology', difficulty: 4 },
    { id: 21, word: 'experience', difficulty: 4 },
    { id: 22, word: 'government', difficulty: 4 },
    { id: 23, word: 'education', difficulty: 4 },
    { id: 24, word: 'community', difficulty: 4 },
    
    // 非常困难单词
    { id: 25, word: 'extraordinary', difficulty: 5 },
    { id: 26, word: 'sophisticated', difficulty: 5 },
    { id: 27, word: 'determination', difficulty: 5 },
    { id: 28, word: 'responsibility', difficulty: 5 },
    { id: 29, word: 'communication', difficulty: 5 },
    { id: 30, word: 'international', difficulty: 5 },
  ];
  
  // 处理滑块变化
  const handleSliderChange = (e) => {
    setWordCount(parseInt(e.target.value));
  };
  
  // 渲染难度指示器
  const renderDifficultyIndicator = () => {
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((level) => (
          <div 
            key={level}
            className={`w-4 h-4 rounded-full ${level <= difficulty ? 'bg-purple-500' : 'bg-gray-200'}`}
            onClick={() => setDifficulty(level)}
          ></div>
        ))}
      </div>
    );
  };
  
  // 随机选择单词
  const generateRandomWords = () => {
    // 根据难度筛选单词
    const filteredWords = randomWordPool.filter(word => {
      // 难度级别上下浮动1级
      return Math.abs(word.difficulty - difficulty) <= 1;
    });
    
    // 随机打乱单词顺序
    const shuffledWords = [...filteredWords].sort(() => Math.random() - 0.5);
    
    // 选择指定数量的单词
    const selectedWords = shuffledWords.slice(0, wordCount).map(word => ({
      id: Date.now() + Math.random(),
      word: word.word
    }));
    
    // 更新全局状态
    setWords(selectedWords);
    
    // 返回主页面
    navigate('/ai-dictation');
  };

  return (
    <div className="min-h-screen bg-[#f9f9f9] text-[#333]">
      {/* iOS Status Bar */}
      <StatusBar />

      {/* Main Content */}
      <div className="p-5 pb-24">
        {/* Navigation Header */}
        <div className="flex justify-between items-center mb-6">
          <Link to="/ai-dictation" className="text-blue-500">
            <i className="fa-solid fa-chevron-left mr-1"></i>
            返回
          </Link>
          <h2 className="text-xl font-bold">随机单词组合</h2>
          <div className="w-16"></div> {/* Spacer for alignment */}
        </div>

        {/* Random Description */}
        <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl p-5 mb-6 text-white shadow-md">
          <div className="flex items-center mb-3">
            <div className="bg-white/20 rounded-full p-2 mr-3">
              <i className="fa-solid fa-shuffle text-xl"></i>
            </div>
            <h3 className="font-bold text-lg">随机单词组合</h3>
          </div>
          <p className="text-white/90 text-sm">
            系统将根据您设置的难度和数量，随机选择单词组合，生成AI短文进行听写练习。
          </p>
        </div>

        {/* Word Count Slider */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-bold text-lg">单词数量</h3>
            <div className="font-medium text-purple-500">{wordCount}个</div>
          </div>
          
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <input 
              type="range" 
              min="3" 
              max="10" 
              value={wordCount} 
              onChange={handleSliderChange}
              className="range-slider mb-2 w-full"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>3</span>
              <span>5</span>
              <span>7</span>
              <span>10</span>
            </div>
          </div>
        </div>

        {/* Difficulty Setting */}
        <div className="mb-8">
          <h3 className="font-bold text-lg mb-3">单词难度</h3>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex justify-between items-center">
              <div>
                <div className="font-medium mb-1">难度级别</div>
                <div className="text-xs text-gray-500">
                  {difficulty === 1 && '简单 - 适合低年级学生'}
                  {difficulty === 2 && '较易 - 适合初学者'}
                  {difficulty === 3 && '中等 - 适合大多数学生'}
                  {difficulty === 4 && '较难 - 适合高年级学生'}
                  {difficulty === 5 && '困难 - 适合挑战自我'}
                </div>
              </div>
              <div className="flex items-center">
                {renderDifficultyIndicator()}
              </div>
            </div>
          </div>
        </div>

        {/* Generate Button */}
        <button 
          className="w-full bg-purple-600 text-white py-4 rounded-xl font-bold text-lg shadow-md flex items-center justify-center"
          onClick={generateRandomWords}
        >
          <i className="fa-solid fa-wand-magic-sparkles mr-2"></i>
          生成随机单词组合
        </button>
      </div>
    </div>
  );
};

export default AiDictationRandom;